import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { site } from "@/data/site";
import { projects } from "@/data/projects";
import { capabilities } from "@/data/capabilities";
import { fieldNotes } from "@/data/fieldNotes";
import { sideProjects } from "@/data/sideProjects";
import { principles } from "@/data/principles";
import { ContactForm } from "@/components/contact-form";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import { ArrowRight, Layers, LayoutTemplate, MessageSquare, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Home() {
  usePageMeta();

  const featuredProjects = projects.filter(p => p.featured);
  const previewNotes = [...fieldNotes].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8 max-w-2xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-foreground">
              I build the systems behind growing businesses.
            </h1>
            <div className="space-y-4 text-lg md:text-xl text-muted-foreground leading-relaxed">
              <p>
                I'm Travis Johnson, a technology and business operator based in Oceanside, California. I turn complicated ideas into working products, platforms, processes, and companies.
              </p>
              <p>
                Part developer, part project manager, part relationship manager, and usually the person people call when something important is stuck.
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2 pt-2">
              {["Technology", "Business Development", "Ecommerce", "Healthcare", "Media"].map(tag => (
                <Badge key={tag} variant="secondary" className="font-normal text-xs uppercase tracking-wider py-1 px-3 bg-secondary text-secondary-foreground">
                  {tag}
                </Badge>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Link href="/work">
                <Button size="lg" className="rounded-full px-8">Explore My Work</Button>
              </Link>
              <Link href="/contact">
                <Button variant="outline" size="lg" className="rounded-full px-8">Get in Touch</Button>
              </Link>
            </div>
          </div>
          
          <div className="relative aspect-[3/5] lg:aspect-[2/3] w-full max-w-sm mx-auto lg:ml-auto rounded-xl overflow-hidden shadow-xl">
            <img
              src="/images/travis-portrait.png"
              alt="Travis Johnson — technology and business operator based in Oceanside, California"
              className="absolute inset-0 w-full h-full object-cover object-top"
              data-testid="img-hero-portrait"
            />
          </div>
        </div>
      </section>

      {/* Operator/Intro Section */}
      <section className="bg-card py-24 border-y border-border">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7 lg:col-span-6 space-y-6">
              <h2 className="text-3xl md:text-4xl font-serif font-medium">More builder than specialist.</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                My work has never fit neatly into one job title. I've written code, designed systems, managed teams, built customer journeys, negotiated with vendors, launched products, troubleshot infrastructure, managed payment relationships, and helped turn rough ideas into operating businesses. 
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                I'm most useful where technology, people, and business decisions collide. I can zoom out to understand the goal, zoom in to find what is broken, and work with the right people to get it fixed.
              </p>
            </div>
            <div className="md:col-span-5 lg:col-span-6 flex justify-center items-center">
              <svg viewBox="0 0 320 300" className="w-full max-w-xs" aria-label="Venn diagram: Technology, Business, and People overlap at Execution">
                <defs>
                  <style>{`
                    .venn-circle { fill-opacity: 0.12; stroke-width: 1.5; }
                    .venn-label { font-family: inherit; font-size: 11px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; fill: currentColor; }
                    .venn-center { font-family: Georgia, serif; font-size: 13px; font-weight: 700; fill: currentColor; }
                  `}</style>
                </defs>
                {/* Technology — top */}
                <circle cx="160" cy="108" r="90" className="venn-circle" stroke="hsl(205 60% 40%)" fill="hsl(205 60% 40%)" />
                {/* Business — bottom left */}
                <circle cx="112" cy="192" r="90" className="venn-circle" stroke="hsl(25 60% 45%)" fill="hsl(25 60% 45%)" />
                {/* People — bottom right */}
                <circle cx="208" cy="192" r="90" className="venn-circle" stroke="hsl(150 40% 38%)" fill="hsl(150 40% 38%)" />

                {/* Labels outside overlap zones */}
                <text x="160" y="30" textAnchor="middle" className="venn-label" opacity="0.7">TECHNOLOGY</text>
                <text x="52" y="264" textAnchor="middle" className="venn-label" opacity="0.7">BUSINESS</text>
                <text x="268" y="264" textAnchor="middle" className="venn-label" opacity="0.7">PEOPLE</text>

                {/* Center label — no background, accent color reads on both themes */}
                <text x="160" y="157" textAnchor="middle" fontFamily="Georgia, serif" fontSize="14" fontWeight="700" letterSpacing="0.02em" style={{ fill: 'var(--accent-foreground, hsl(205 60% 55%))' }} opacity="1">Execution</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* What I Do Section */}
      <section id="what-i-do" className="container mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">What I Do</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {[
            {
              title: "Build Digital Products",
              icon: <LayoutTemplate className="w-6 h-6 text-accent" />,
              desc: "Websites, ecommerce platforms, customer portals, healthcare intake systems, internal dashboards, APIs, automations, databases, tracking systems, custom business tools."
            },
            {
              title: "Untangle Complex Systems",
              icon: <Layers className="w-6 h-6 text-accent" />,
              desc: "Often brought in when there are too many vendors, disconnected platforms, unreliable data, compliance concerns, or unclear ownership. Map the system, find the bottlenecks, create a workable path forward."
            },
            {
              title: "Lead Projects and People",
              icon: <MessageSquare className="w-6 h-6 text-accent" />,
              desc: "Coordinate developers, designers, marketers, attorneys, healthcare partners, financial institutions, vendors, and internal teams. Translate between groups that don't always speak the same language."
            },
            {
              title: "Grow and Operate Businesses",
              icon: <ShieldAlert className="w-6 h-6 text-accent" />,
              desc: "Help evaluate opportunities, launch products, improve customer experiences, manage partnerships, solve operational problems, build systems that can support growth."
            }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-2xl bg-secondary/50 border border-border/50 hover:bg-secondary transition-colors">
              <div className="w-12 h-12 rounded-full bg-background flex items-center justify-center mb-6 shadow-sm border border-border">
                {item.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Selected Work Section */}
      <section className="bg-card py-24 border-y border-border">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-3xl">
              <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">Selected Work</h2>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-2xl italic border-l-2 border-accent pl-4 py-1">
                Much of my work happens behind the scenes, and some projects involve private systems or confidential partnerships. These examples focus on the problems, responsibilities, and systems involved rather than exposing private business information.
              </p>
            </div>
            <Link href="/work" className="shrink-0">
              <Button variant="outline" className="rounded-full">View All Work <ArrowRight className="ml-2 w-4 h-4" /></Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {featuredProjects.map(project => (
              <Link key={project.slug} href={`/work/${project.slug}`}>
                <div className="group block h-full p-8 rounded-2xl bg-background border border-border transition-all hover:shadow-lg hover:border-accent/30 flex flex-col cursor-pointer">
                  <div className="flex justify-between items-start mb-4">
                    <Badge variant="outline" className="bg-secondary/50 text-xs font-medium tracking-wide">
                      {project.industry}
                    </Badge>
                  </div>
                  <h3 className="text-2xl font-serif font-medium mb-2 group-hover:text-accent transition-colors">{project.name}</h3>
                  <p className="text-sm font-medium text-foreground/80 mb-6">{project.role}</p>
                  <p className="text-muted-foreground mb-8 flex-1 leading-relaxed">{project.summary}</p>
                  
                  <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-border/50">
                    {project.areas.slice(0, 3).map(area => (
                      <span key={area} className="text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded-md">
                        {area}
                      </span>
                    ))}
                    {project.areas.length > 3 && (
                      <span className="text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded-md">
                        +{project.areas.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How I Work Section */}
      <section className="container mx-auto px-6 py-24">
        <h2 className="text-3xl md:text-4xl font-serif font-medium mb-16 max-w-2xl">I don't just hand problems to someone else.</h2>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {[
            { num: "01", title: "Understand the actual goal", desc: "Figure out what the business is genuinely trying to accomplish." },
            { num: "02", title: "Map the whole system", desc: "Customer, technology, data, people, vendors, constraints, downstream consequences." },
            { num: "03", title: "Find the real bottleneck", desc: "The loudest problem is not always the root problem." },
            { num: "04", title: "Build the practical solution", desc: "The best system is the one the team can operate and improve." },
            { num: "05", title: "Stay involved through execution", desc: "Work through messy implementation details until the system functions." }
          ].map((step, i) => (
            <div key={i} className="relative">
              <div className="text-5xl font-serif font-light text-accent/20 mb-4">{step.num}</div>
              <h3 className="text-lg font-semibold mb-2 pr-4">{step.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              {i < 4 && <div className="hidden lg:block absolute top-6 right-0 w-[calc(100%-2rem)] h-[1px] bg-border -z-10" />}
            </div>
          ))}
        </div>
      </section>

      {/* Experience Snapshot Section */}
      <section className="bg-foreground text-background py-24 dark:bg-card dark:text-foreground border-y border-border">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 justify-between">
            <div className="max-w-xl">
              <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">Two decades of figuring things out.</h2>
              <p className="text-background/80 dark:text-muted-foreground text-lg leading-relaxed mb-8">
                My career path isn't a straight line of promotions within a single discipline. It's a collection of deep dives into whatever domain was required to build the business or solve the problem at hand.
              </p>
              <Link href="/experience">
                <Button variant="secondary" className="rounded-full">View Professional Background</Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 lg:w-1/2">
              {[
                "Building/operating internet businesses", 
                "Technology/software development", 
                "Ecommerce/digital marketing", 
                "Business development/strategic partnerships", 
                "Payment processing/risk management", 
                "Healthcare/telehealth technology", 
                "Digital media/content systems", 
                "Team/project leadership", 
                "Vendor/client/stakeholder management"
              ].map(theme => (
                <div key={theme} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                  <span className="text-sm font-medium text-background/90 dark:text-foreground/90">{theme}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Personal Section */}
      <section className="container mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">The person behind the projects.</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                I live in Oceanside, California with my wife, our two kids, and our dog Butters (sometimes affectionately known as Goose). 
              </p>
              <p>
                When I'm not in front of a screen solving business problems, I'm usually cycling, working out, or building something physical. I approach hobbies the same way I approach work: figure out how it works, optimize it, and don't be afraid to get my hands dirty.
              </p>
              <p>
                I try to take the work seriously without taking myself too seriously.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 grid-rows-2 gap-4 h-[500px]">
            {/* Left: cycling selfie — tall portrait crop */}
            <div className="row-span-2 rounded-xl overflow-hidden relative border border-border">
              <img
                src="/images/cycling.png"
                alt="Travis and a friend at a cycling event in Oceanside"
                className="absolute inset-0 w-full h-full object-cover object-right-top"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-3">
                <span className="text-xs font-medium uppercase tracking-wider text-white/90">Cycling</span>
              </div>
            </div>
            {/* Top right: family at dinner */}
            <div className="rounded-xl overflow-hidden relative border border-border">
              <img
                src="/images/family-dinner.png"
                alt="Travis with his family at dinner"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-3">
                <span className="text-xs font-medium uppercase tracking-wider text-white/90">Family</span>
              </div>
            </div>
            {/* Bottom right: Butters */}
            <div className="rounded-xl overflow-hidden relative border border-border">
              <img
                src="/images/butters.png"
                alt="Butters, aka Goose"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-3">
                <span className="text-xs font-medium uppercase tracking-wider text-white/90">Butters aka Goose</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Side Projects Section */}
      <section className="bg-card py-24 border-y border-border">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">Things I build because they sounded interesting.</h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {sideProjects.map((project, i) => (
              <div key={i} className="group p-6 rounded-2xl bg-background border border-border hover:border-accent/30 transition-all flex flex-col h-full">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-medium">
                    {project.status}
                  </Badge>
                </div>
                <h3 className="text-lg font-semibold mb-2">{project.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  {project.description}
                </p>
                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
                  >
                    {project.linkLabel} <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cancer Awareness Section */}
      <section className="py-24 border-y border-border" style={{ background: 'hsl(var(--accent) / 0.06)' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-12 md:gap-16 items-start">
              <div className="md:w-1/3 shrink-0">
                <p className="text-6xl md:text-7xl font-serif font-medium text-accent leading-none mb-3">1 in 23</p>
                <p className="text-base text-muted-foreground font-medium leading-snug">men will be diagnosed with<br/>colorectal cancer in their lifetime</p>
              </div>
              <div className="md:w-2/3 space-y-5">
                <h2 className="text-2xl md:text-3xl font-serif font-medium leading-snug">In April 2025, I was diagnosed with stage 3 colorectal cancer. I want to talk about it.</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Three months of chemo. Surgery. It came back. Four more months of chemo. Now I'm in monitoring. It has been the hardest year of my life, and also the one that made me realize how many men have no idea this kind of thing is coming.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Colorectal cancer is one of the most preventable and treatable cancers when caught early. But men avoid the doctor. They ignore symptoms. They wait. I was 40 when I got my diagnosis, and now every man I meet around that age hears my story, because a conversation and a screening can genuinely save a life.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  If you're reading this and you've recently been diagnosed, or you're scared and don't know where to start, reach out. I'm not a doctor, but I've been through it, and sometimes you just need to talk to someone who gets it.
                </p>
                <div className="pt-2">
                  <Link href="/contact">
                    <Button className="rounded-full">
                      Reach Out <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section className="container mx-auto px-6 py-24 md:py-32">
        <h2 className="text-3xl md:text-4xl font-serif font-medium mb-16 text-center">A few things I believe.</h2>
        <div className="max-w-4xl mx-auto space-y-12">
          {principles.map((principle, i) => (
            <div key={i} className="flex gap-6 md:gap-8 items-start">
              <span className="text-lg md:text-xl font-serif text-accent shrink-0 mt-1">{String(i + 1).padStart(2, '0')}</span>
              <p className="text-2xl md:text-3xl font-serif font-medium leading-tight text-foreground/90">"{principle}"</p>
            </div>
          ))}
        </div>
      </section>

      {/* Field Notes Preview — hidden until real entries are ready */}
      {false && (
      <section className="bg-card py-24 border-y border-border">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">Field Notes</h2>
              <p className="text-muted-foreground text-lg">Observations on technology, business, and operations.</p>
            </div>
            <Link href="/field-notes" className="shrink-0">
              <Button variant="outline" className="rounded-full">Read More Notes <ArrowRight className="ml-2 w-4 h-4" /></Button>
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {previewNotes.map(note => (
              <Link key={note.slug} href={`/field-notes/${note.slug}`}>
                <article className="group p-8 rounded-2xl bg-background border border-border hover:border-accent/30 hover:shadow-md transition-all h-full flex flex-col cursor-pointer">
                  <div className="flex items-center gap-3 mb-4">
                    <time className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {new Date(note.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </time>
                    {note.draft && <Badge variant="secondary" className="text-[10px]">Draft</Badge>}
                  </div>
                  <h3 className="text-xl font-serif font-medium mb-3 group-hover:text-accent transition-colors">{note.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-1 line-clamp-3">
                    {note.excerpt}
                  </p>
                  <div className="flex items-center text-sm font-medium text-foreground mt-auto">
                    Read article <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="bg-secondary/30 py-24 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-card p-8 md:p-12 rounded-3xl shadow-sm border border-border">
            <div className="mb-10 text-center max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-serif font-medium mb-4">Have something complicated?</h2>
              <p className="text-muted-foreground text-lg">
                I'm always interested in meeting good people, hearing about unusual projects, and helping solve problems that sit somewhere between technology and business.
              </p>
            </div>
            
            <ContactForm />
          </div>
        </div>
      </section>
    </Layout>
  );
}
